/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react'
import ReactDOM from 'react-dom';
import { DateCompareToMenu } from './Components/DateCompareToMenu';
import { ApplicationsMenu } from './Components/ApplicationsMenu';
import { TAnalyticsPayload, TAppParentMenuItem, TAppSubMenuItem, TMerchantData } from './analytics';

import { DateSelectionMenu } from './Components/DateSelectionMenu';
import HorizontalLine from 'Components/Common/components/Button/HorizontalLine';
import { BackArrowIcon, XIcon } from 'assests/GmsIcons';
import { useHistory } from 'react-router-dom';

import { AnalyticsContext } from './Context/AnalyticsContext';
import MerchantsMenu from './Components/MerchantsMenu';
import { adjustDateRangeWithCurrentTime, calculatePreviousPeriod, getFormattedDateTime, getDateWithMidnight, getStartAndEndOfPreviousYear } from './AnalyticsHelper';

import DateRangePickerDialog from './Components/DatePickerDialog';
import { DateRange } from './Components/types';
import { downloadArsReport, downloadSummaryReport, fetchAllMetrics } from './Druid/DruidServices';
import { calculateDifferences, formatDate, getDateRangeBasedTimeGrain, upperBoundDateformat } from './MetricsHelper';
import { COMMEND_REPORT, GENERATE_REPORT_MESSAGE, LAST_YEAR_1M, NONE, SUMMARY_REPORT, THIS_YEAR_1M, THIS_YEAR_1W, THIS_YEAR_2W, TIME_ZONE_LIMIT_MESSAGE, TODAY } from './AnalyticsConstants';
import { AppContext } from 'Context/AppContext';
import { TimezoneMenu } from './Components/TimezoneMenu';
import { ReportMenu } from './Components/ReportMenu';
import { getMerchantsBasedOnPartyAndIndustry } from 'Components/Templates/Searchbar/APIServices/GMSServices';
import { toast } from 'react-toastify';

type TAnalyticsHeaderProps = {
    detailPage?: boolean;
    pageTitle: string;
}

const AnalyticsHeader = ({ detailPage = false, pageTitle }: TAnalyticsHeaderProps) => {
    const history = useHistory();
    const context = useContext(AnalyticsContext);
    const appContext = useContext<any>(AppContext);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [downloadUrl, setDownloadUrl] = useState<string>('');
    const closeDialog = () => setShowDialog(false);
    const isUpdatingPayload = useRef(false);
    const limitedTimeRanges = [THIS_YEAR_1M, THIS_YEAR_1W, THIS_YEAR_2W, LAST_YEAR_1M];

    const partyId = useMemo(() => {
        try {
            const storedPartyId = localStorage.getItem("current_party_id");
            return storedPartyId ? JSON.parse(storedPartyId) : null;
        } catch {
            return null;
        }
    }, [localStorage.getItem("current_party_id")]);

    const industryId = useMemo(() =>
        appContext.get_selected_industry()?.id || null
        , [appContext.get_selected_industry()]);
    const merchantsLoaded = useRef(false);

    const getMerchants = useCallback(async () => {

        if (!partyId || !industryId) return;
        if (!merchantsLoaded.current && context?.merchantData?.length !== 0) return

        try {
            const response = await getMerchantsBasedOnPartyAndIndustry(partyId, industryId);
            if (response?.data?.done) {
                const channelList = response?.data?.data?.map((item: any) => ({
                    merchant_key: item.merchant_key,
                    user_name: item.user_name
                })).sort((a: TMerchantData, b: TMerchantData) => a.user_name.localeCompare(b.user_name));

                context.setMerchantData(channelList ?? []);

                if (channelList?.length > 0) {
                    context.setSelectedMerchant(channelList[0]);
                } else {
                    context.setSelectedMerchant(undefined);
                }

                merchantsLoaded.current = true;
            }
        } catch (error) {
            console.error("Error getting merchants:", error);
        }
    }, [partyId, industryId, context.setMerchantData, context.setSelectedMerchant]);

    useEffect(() => {
        if (partyId && industryId && !merchantsLoaded.current) {
            getMerchants();
        }
    }, [partyId, industryId, getMerchants]);

    useEffect(() => {
        merchantsLoaded.current = false;
    }, [partyId, industryId]);

    const handleApplicationClick = (item: TAppParentMenuItem, index: number) => {
        const updatedData = [...context.menuData];
        updatedData[index].checked = !item.checked;
        context.setMenuData(updatedData);
    }

    const removeSelectedBuilder = (subItem: TAppSubMenuItem) => {
        context.setSelectedSubMenuItems((prev: TAppSubMenuItem[]) => prev.filter(item => item.value !== subItem.value));
    };

    const removeSelectedArsBox = (subItem: TAppSubMenuItem) => {
        context.setSelectedCommendSubMenuItems((prev: TAppSubMenuItem[]) => prev.filter(item => item.value !== subItem.value));
    };

    useEffect(() => {
        if (!partyId || !industryId) return;

        if (isUpdatingPayload.current) return;
        isUpdatingPayload.current = true;

        try {
            const builderIds = context.selectedSubMenuItems.map((item: TAppSubMenuItem) => item.value);
            const boxIds = context.selectedCommendSubMenuItems.map((item: TAppSubMenuItem) => item.value);

            if (builderIds.length === 0) {
                const updatedData = context.menuData.map((menu: TAppParentMenuItem) => {
                    if (menu.value === "Guide") {
                        return { ...menu, checked: false };
                    }
                    return menu
                });
                context.setMenuData(updatedData);
            }

            if (boxIds.length === 0) {
                const updatedData = context.menuData.map((menu: TAppParentMenuItem) => {
                    if (menu.value === "ARS") {
                        return { ...menu, checked: false };
                    }
                    return menu
                });
                context.setMenuData(updatedData);
            }

            if (builderIds.length > 0 && context.selectedCompareMenuItem !== NONE) {
                const updatedData = context.menuData.map((menu: TAppParentMenuItem) => {
                    if (menu.value === "Guide") {
                        return { ...menu, checked: true };
                    } else {
                        return { ...menu, checked: false };
                    }
                });
                context.setMenuData(updatedData);
            }

            if (boxIds.length > 0 && context.selectedCompareMenuItem !== NONE) {
                const updatedData = context.menuData.map((menu: TAppParentMenuItem) => {
                    if (menu.value === "ARS") {
                        return { ...menu, checked: true };
                    } else {
                        return { ...menu, checked: false };
                    }
                });
                context.setMenuData(updatedData);
            }

            setTimeout(() => {
                const titles = context.menuData
                    .filter((item: TAppSubMenuItem) => item.checked)
                    .map((item: TAppSubMenuItem) => item.value);

                let newApplications = [...titles];

                const hasSelectedItems = builderIds.length > 0;
                const hasConfigure = newApplications.includes('Guide');

                const hasSelectedCommendItems = boxIds.length > 0;
                const hasARS = newApplications.includes('ARS');

                if (hasSelectedItems && !hasConfigure) {
                    newApplications.push('Guide');
                } else if (!hasSelectedItems) {
                    newApplications = newApplications.filter(app => app !== 'Guide');
                }

                if (hasSelectedCommendItems && !hasARS) {
                    newApplications.push('ARS');
                } else if (!hasSelectedCommendItems) {
                    newApplications = newApplications.filter(app => app !== 'ARS');
                }

                context.setPayload((prev: TAnalyticsPayload) => ({
                    ...prev,
                    builder_ids: builderIds,
                    box_ids: boxIds,
                    applications: newApplications?.length === 0 ? ["Compatio"] : newApplications
                }));

                isUpdatingPayload.current = false;
            }, 0);
        } catch (error) {
            console.error("Error updating payload:", error);
            isUpdatingPayload.current = false;
        }
    }, [context.menuData, context.selectedSubMenuItems, context.selectedCommendSubMenuItems, context.selectedCompareMenuItem]);


    useEffect(() => {
        const updateState = { ...context.payload, merchant_key: context?.selectedMerchant?.merchant_key ? [context?.selectedMerchant?.merchant_key] : [] }
        context.setPayload(updateState);
    }, [context.selectedMerchant]);

    useEffect(() => {
        const updateState = { ...context.payload, timezone: context?.selectedTimezone?.name, }
        context.setPayload(updateState);
    }, [context?.selectedTimezone]);

    useEffect(() => {
        if (detailPage) return;
        setTimeout(() => {
            context.setComingFromDetailPage(false);
        }, 2000);
        context.ensureDefaultApplication()
        if (context?.payload?.applications?.length > 0 && context?.payload?.dateRange?.length > 0 && context?.payload?.merchant_key?.length > 0) {
            if (context?.comingFromDetailPage) return;
            callApis();
        } else {
            context?.clearGraphAndMetricsData();
        }
    }, [context.payload]);

    function updateDateRange() {
        const dateRange = context.payload.dateRange;
        dateRange[1] = getFormattedDateTime(context?.selectedTimezone?.name);
        const updateState = { ...context.payload, dateRange: dateRange };
        return updateState;
    }

    const generateReport = async (reportType: string) => {
        if (limitedTimeRanges.includes(context?.selectedItem.label)) {
            toast.warning(TIME_ZONE_LIMIT_MESSAGE);
            return;
        }
        if (context.payload.applications.length > 0 && context.payload.dateRange.length > 0 && context.payload.merchant_key.length > 0) {
            let payload = { ...context.payload };
            if (context?.selectedItem.label === TODAY) {
                payload = updateDateRange()
            }

            try {
                toast.info(GENERATE_REPORT_MESSAGE);
                const downloadFunction = reportType === SUMMARY_REPORT ? downloadSummaryReport : downloadArsReport;
                const response = await downloadFunction(payload);
                if (response !== null) {
                    setDownloadUrl(response?.data?.s3_url);
                }
            } catch (error) {
                console.error('Error downloading file:', error);
                toast.error("Downloading failed: " + error);
            } finally {
                setTimeout(() => {
                    setDownloadUrl('');
                }, 4000);
            }
        } else {
            toast.info('Date, Channel, and Applications are required');
        }
    };

    const callApis = async () => {

        context.ensureDefaultApplication();
        await fetchMetricsData();
        await context.fetchGraphData();
    }

    const fetchMetricsData = async () => {

        try {
            context.setShowLoader(true);
            let dateRange = getDateRangeBasedTimeGrain(context?.selectedItem?.label, context?.selectedTimezone?.name);
            if (context.selectedCompareMenuItem !== NONE) {
                let preDateRange = [];
                if (context?.selectedItem.label === THIS_YEAR_1M || context?.selectedItem.label === THIS_YEAR_1W) {
                    const { startOfYear, endOfYear } = getStartAndEndOfPreviousYear(context?.selectedTimezone?.name);
                    preDateRange = [formatDate(startOfYear, context?.selectedTimezone?.name), upperBoundDateformat(endOfYear, context?.selectedTimezone?.name)];
                } else if (context?.selectedItem.label === TODAY) {
                    preDateRange = calculatePreviousPeriod(dateRange, context.payload.granularity, context?.selectedItem.label, context?.selectedTimezone?.name);
                    dateRange = [getDateWithMidnight(context?.selectedTimezone?.name), getFormattedDateTime(context?.selectedTimezone?.name)];
                } else {
                    preDateRange = calculatePreviousPeriod(dateRange, context.payload.granularity, context?.selectedItem.label, context?.selectedTimezone?.name);
                }
                const updateStateCP = { ...context.payload, dateRange: dateRange };
                const updateState = { ...context.payload, dateRange: preDateRange };
                const currentPeriodData = await fetchAllMetrics(updateStateCP);
                if (currentPeriodData === null) {
                    return;
                }
                const previousPeriodData = await fetchAllMetrics(updateState);
                if (previousPeriodData === null) {
                    return;
                }
                const result = calculateDifferences(currentPeriodData, previousPeriodData);
                context.setApplicationMetrics(result);
            } else {
                let updatedDateRange = dateRange;
                if (context?.selectedItem.label === TODAY) {
                    updatedDateRange = [getDateWithMidnight(context?.selectedTimezone?.name), getFormattedDateTime(context?.selectedTimezone?.name)];
                } else if ([THIS_YEAR_1M, THIS_YEAR_1W, THIS_YEAR_2W].includes(context?.selectedItem.label)) {
                    updatedDateRange = adjustDateRangeWithCurrentTime(context.payload.dateRange, context?.selectedTimezone?.name);
                }
                const updateState = { ...context.payload, dateRange: updatedDateRange };
                const response = await fetchAllMetrics(updateState);
                if (response === null) {
                    context.setApplicationMetrics([]);
                    return;
                }
                context.setApplicationMetrics(response);
            }
        } catch (error) {
            console.error("Metric Data ", error);
            context.setApplicationMetrics([]);
        } finally {
            context.setShowLoader(false);
        }
    }

    const handleDateRangeChange = (dateRange: DateRange) => {
        const [formattedStartDate, formattedEndDate, formattedString] = formatDates(dateRange.startDate, dateRange.endDate);
        context.setSelectedItem({ label: "Custom", value: formattedString, });
        const updateState = { ...context.payload, dateRange: [formattedStartDate, formattedEndDate], }
        context.setPayload(updateState);
    };

    function formatDatePicker(date: Date): string {
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        return `${day} ${month}`;
    }

    function formatDates(startDate: Date, endDate: Date): [string, string, string] {
        const formattedStart = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')} 00:00:00`;
        const formattedEnd = `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${endDate.getDate().toString().padStart(2, '0')} 00:00:00`;
        const formattedString = `${formatDatePicker(startDate)} - ${formatDatePicker(endDate)}`;
        return [formattedStart, formattedEnd, formattedString];
    }

    const isARSChecked = (): boolean => {
        const arsItem = context?.menuData?.find((item: TAppParentMenuItem) => item.title === 'Commend (ARS)');
        return arsItem ? arsItem.checked : false;
    }

    const checkedItemsCount = useMemo(() =>
        context.menuData?.filter((item: TAppParentMenuItem) => item.checked).length || 0,
        [context.menuData]
    );

    const canRemoveItem = useCallback((itemTitle: string) => {
        return !(itemTitle === "Compatio" && checkedItemsCount <= 1 && context?.selectedSubMenuItems?.length === 0 && context?.selectedCommendSubMenuItems?.length === 0);
    }, [checkedItemsCount, context?.selectedSubMenuItems, context?.selectedCommendSubMenuItems]);

    const modalRoot = document.querySelector('#modal-root');


    const renderSelectedItemsInOrder = () => {
        const items: JSX.Element[] = [];

        items.push(
            <label key="applications-label" className={`tw-font-semibold tw-text-xs tw-text-[#6B778C]`}>
                Applications
            </label>
        );
        
        context.menuData?.forEach((menuItem: TAppParentMenuItem, menuIndex: number) => {

            if (menuItem.checked && menuItem.title !== "Guide" && menuItem.title !== "Commend (ARS)") {

                const isRemovable = canRemoveItem(menuItem.title);
                items.push(
                    <div
                        key={`menu-${menuItem.title}-${menuIndex}`}
                        onClick={() => isRemovable ? handleApplicationClick(menuItem, menuIndex) : null}
                        className={`tw-rounded tw-bg-[#FFF] tw-cursor-pointer tw-flex tw-flex-row tw-py-1 tw-px-2 tw-items-center tw-justify-start tw-gap-[4px] tw-text-slategray-200 tw-font-noto-sans tw-border-[1px] tw-border-solid tw-border-[#DFE1E6]`}
                    >
                        <label className={`tw-text-xs tw-cursor-pointer tw-font-medium`}>{menuItem.title}</label>
                        {isRemovable ? <XIcon /> : null}
                    </div>
                );
            }


            if (menuItem.value === "Guide" && context?.selectedSubMenuItems?.length > 0) {

                items.push(
                    <label key="guide-label" className={`tw-font-semibold tw-text-xs tw-text-[#6B778C]`}>
                        Guide
                    </label>
                );


                context.selectedSubMenuItems.forEach((subItem: TAppSubMenuItem, subIndex: number) => {
                    if (subItem.checked) {
                        items.push(
                            <div
                                key={`guide-${subItem.value}-${subIndex}`}
                                onClick={() => removeSelectedBuilder(subItem)}
                                className={`tw-rounded tw-bg-[#FFF] tw-cursor-pointer tw-flex tw-flex-row tw-py-1 tw-px-2 tw-items-center tw-justify-start tw-gap-[4px] tw-text-slategray-200 tw-font-noto-sans tw-border-[1px] tw-border-solid tw-border-[#DFE1E6]`}
                            >
                                <label className={`tw-text-xs tw-cursor-pointer tw-font-medium`}>{subItem?.title}</label>
                                <XIcon />
                            </div>
                        );
                    }
                });
            }


            if (menuItem.value === "ARS" && context?.selectedCommendSubMenuItems?.length > 0) {

                items.push(
                    <label key="ars-label" className={`tw-font-semibold tw-text-xs tw-text-[#6B778C]`}>
                        Commend (ARS)
                    </label>
                );


                context.selectedCommendSubMenuItems
                    .slice()
                    .sort((a: TAppSubMenuItem, b: TAppSubMenuItem) => a.title.localeCompare(b.title))
                    .forEach((subItem: TAppSubMenuItem, subIndex: number) => {
                        if (subItem.checked) {
                            items.push(
                                <div
                                    key={`ars-${subItem.value}-${subIndex}`}
                                    onClick={() => removeSelectedArsBox(subItem)}
                                    className={`tw-rounded tw-bg-[#FFF] tw-cursor-pointer tw-flex tw-flex-row tw-py-1 tw-px-2 tw-items-center tw-justify-start tw-gap-[4px] tw-text-slategray-200 tw-font-noto-sans tw-border-[1px] tw-border-solid tw-border-[#DFE1E6]`}
                                >
                                    <label className={`tw-text-xs tw-cursor-pointer tw-font-medium`}>{subItem?.title}</label>
                                    <XIcon />
                                </div>
                            );
                        }
                    });
            }
        });

        return items;
    };

    return (
        <React.Fragment>
            {(showDialog && modalRoot) && ReactDOM.createPortal(<DateRangePickerDialog onChange={handleDateRangeChange} onClose={closeDialog} isOpen={showDialog} />, modalRoot)}
            <div className={`tw-flex tw-flex-wrap tw-justify-between`}>
                <div className='tw-flex tw-items-center tw-gap-2 tw-cursor-pointer' onClick={() => { if (detailPage) { history.replace('/gms/performance'); context.setComingFromDetailPage(true); } }}>
                    {detailPage ? <BackArrowIcon /> : null}
                    <label className={`tw-font-semibold tw-cursor-pointer tw-flex tw-flex-row tw-py-0 tw-pr-2.5 tw-pl-0 tw-items-center tw-justify-start tw-text-[18px] tw-text-[#091E42]`}>{detailPage ? pageTitle : 'Performance'}</label>
                </div>
                <div className={`tw-flex`}>
                    <ReportMenu onSummaryReportClick={() => generateReport(SUMMARY_REPORT)} onARSReportClick={() => generateReport(COMMEND_REPORT)} isARSAvailable={isARSChecked()} />
                </div>
            </div>
            <div className={`tw-flex tw-flex-wrap tw-items-center tw-text-center tw-h-12 tw-justify-between`}>
                <div className={`tw-flex tw-gap-1`}>
                    <DateSelectionMenu analyticsPayload={context.payload} setAnalyticsPayload={context.setPayload} selectedItem={context?.selectedItem} setSelectedItem={context?.setSelectedItem} timezone={context.selectedTimezone?.abbreviation} selectedTimezone={context.selectedTimezone} selectedRange={context?.selectedRange} setSelectedRange={context?.setSelectedRange} />
                    <TimezoneMenu />
                    <DateCompareToMenu analyticsPayload={context.payload} setAnalyticsPayload={context.setPayload} selectedCompareMenuItem={context.selectedCompareMenuItem} setSelectedCompareMenuItem={context.setSelectedCompareMenuItem} />
                </div>
                <div className={`tw-flex`}>
                    <MerchantsMenu merchantData={context.merchantData} selectedMerchant={context.selectedMerchant} setSelectedMerchant={context.setSelectedMerchant} />
                    <ApplicationsMenu menuData={context.menuData} setMenuData={context.setMenuData} />
                </div>
            </div>
            <HorizontalLine />
            <div className={`tw-flex tw-flex-wrap tw-items-center tw-bg-[#F7F7F7] tw-text-center tw-py-2 tw-ps-2 tw-gap-2`}>
                {renderSelectedItemsInOrder()}
            </div>
            {modalRoot && <iframe style={{ display: "none" }} src={downloadUrl} id="iframe_download_report" title="Download Report" />}
        </React.Fragment>
    );
}

export default AnalyticsHeader